import { ChangeEvent } from 'react';

import {
  Button,
  Checkbox,
  Chip,
  ListItemIcon,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { Autocomplete } from '@material-ui/lab';

import isNil from 'lodash/isNil';

import {
  ChipInput,
  ChipTimestampPicker,
  ContextMenu,
  CountryAutocomplete,
  ObjUtils,
} from '@spotify/confidence-core-react';
import {
  MetricFilterCriterionFragment,
  MetricsV1ColumnType,
  MetricsV1FilterEqRuleInput,
  MetricsV1FilterLikeRuleInput,
  MetricsV1FilterRangeRule,
  MetricsV1FilterSetRuleInput,
} from '@spotify/confidence-graphql';

import { OperatorOption, RangeOperatorOption, ValueType } from './Criteria';
import { FilterColumn } from './types';

const useStyles = makeStyles(theme => ({
  listItemIcon: {
    minWidth: 'auto',
    marginRight: theme.spacing(1),
  },
  checkbox: {
    marginLeft: 0,
    paddingLeft: 0,
  },
}));

const formatValue =
  (valueType: ValueType, hll?: boolean) => (v: string | number) => {
    if (valueType === 'numberValue' && v !== '') {
      const n = Number(v);
      return hll ? Math.trunc(n) : n;
    }
    return v;
  };

type RuleInputProps<T> = {
  valueType: ValueType;
  attribute: string;
  value?: T | null;
  onChange: (v: Partial<MetricFilterCriterionFragment>) => void;
  column?: FilterColumn;
};
export const MetricFilterEqRule = ({
  valueType,
  value,
  attribute,
  onChange,
  column,
}: RuleInputProps<MetricsV1FilterEqRuleInput>) => {
  const semanticType = column?.semanticType;
  const hll = column?.type === MetricsV1ColumnType.ColumnTypeHllSketch;
  if (valueType === 'boolValue') {
    return 'true';
  }
  if (valueType === 'nullValue') {
    return null;
  }
  const v = value?.value[valueType] ?? null;

  if (valueType === 'timestampValue') {
    return (
      <ChipTimestampPicker
        maxWidth={120}
        displayFormat="yyyy-MM-dd HH:mm"
        value={v ? v.toString() : null}
        onChange={newValue => {
          onChange({
            attribute: {
              attribute,
              eqRule: {
                value: {
                  [valueType]: newValue,
                },
              },
            },
          });
        }}
      />
    );
  }

  if (semanticType?.type === 'enum_type' && valueType === 'stringValue') {
    return (
      <ChipInput
        select
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          onChange({
            attribute: {
              attribute,
              eqRule: {
                value: {
                  [valueType]: newValue,
                },
              },
            },
          });
        }}
        value={v || ''}
      >
        {semanticType.options.map(opt => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </ChipInput>
    );
  }

  if (semanticType?.type === 'country_code' && valueType === 'stringValue') {
    return (
      <CountryAutocomplete
        value={(v as string) || null}
        onChange={newValue => {
          onChange({
            attribute: {
              attribute,
              eqRule: {
                value: {
                  [valueType]: newValue as string | null,
                },
              },
            },
          });
        }}
      />
    );
  }

  return (
    <ChipInput
      placeholder={valueType.replace('Value', '')}
      type={valueType === 'numberValue' ? 'number' : 'text'}
      inputProps={hll ? { step: '1' } : undefined}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        const formattedValue = formatValue(valueType, hll)(newValue);
        onChange({
          attribute: {
            attribute,
            eqRule: {
              value: {
                [valueType]: formattedValue,
              },
            },
          },
        });
      }}
      value={v}
    />
  );
};

export const MetricFilterSetRule = ({
  valueType,
  value,
  attribute,
  onChange,
  column,
}: RuleInputProps<MetricsV1FilterSetRuleInput>) => {
  const semanticType = column?.semanticType;
  const hll = column?.type === MetricsV1ColumnType.ColumnTypeHllSketch;
  const classes = useStyles();
  // Filter out empty values to avoid leading comma in display
  const values = (value?.values ?? [])
    .map(v => v[valueType]?.toString())
    .filter((v): v is string => Boolean(v));

  if (semanticType?.type === 'enum_type' && valueType === 'stringValue') {
    return (
      <ChipInput
        select
        SelectProps={{
          multiple: true,
          renderValue: (selected: unknown) => {
            const selectedValues = selected as string[];
            const limit = 4;
            if (selectedValues.length <= limit) {
              return selectedValues.join(', ');
            }
            const visible = selectedValues.slice(0, limit).join(', ');
            const remaining = selectedValues.length - limit;
            return `${visible} +${remaining} more`;
          },
        }}
        onChange={(e: ChangeEvent<{ value: unknown }>) => {
          const newValues = e.target.value as string[];
          onChange({
            attribute: {
              attribute,
              setRule: {
                values: newValues.map(v => ({ [valueType]: v })),
              },
            },
          });
        }}
        value={values}
      >
        {semanticType.options.map(opt => (
          <MenuItem key={opt} value={opt}>
            <ListItemIcon className={classes.listItemIcon}>
              <Checkbox
                checked={values.includes(opt)}
                color="primary"
                size="small"
                className={classes.checkbox}
              />
            </ListItemIcon>
            {opt}
          </MenuItem>
        ))}
      </ChipInput>
    );
  }

  if (semanticType?.type === 'country_code' && valueType === 'stringValue') {
    return (
      <CountryAutocomplete
        multiple
        value={values}
        onChange={newValues => {
          onChange({
            attribute: {
              attribute,
              setRule: {
                // Keep at least one empty value to maintain valueType structure
                values:
                  (newValues as string[]).length > 0
                    ? (newValues as string[]).map(v => ({ [valueType]: v }))
                    : [{ [valueType]: '' }],
              },
            },
          });
        }}
      />
    );
  }

  return (
    <Autocomplete
      disableClearable
      autoSelect
      renderInput={params => (
        <ChipInput
          {...params}
          InputProps={{
            ...params.InputProps,
            type: valueType === 'numberValue' ? 'number' : 'tex',
          }}
          inputProps={{
            ...params.inputProps,
            ...(hll ? { step: '1' } : undefined),
          }}
          placeholder={valueType.replace('Value', '')}
        />
      )}
      renderTags={(tagValues, getTagProps) =>
        tagValues.map((option, index) => (
          <Chip
            key={option}
            size="small"
            label={option}
            {...getTagProps({ index })}
            disabled={false}
          />
        ))
      }
      multiple
      options={[] as string[]}
      freeSolo
      value={values}
      onChange={(_e, newV) => {
        onChange({
          attribute: {
            attribute,
            setRule: {
              values: (newV ?? []).map(v => ({
                [valueType]: formatValue(valueType, hll)(v),
              })),
            },
          },
        });
      }}
    />
  );
};

export const MetricFilterLikeRule = ({
  attribute,
  value,
  onChange,
}: {
  attribute: string;
  value?: MetricsV1FilterLikeRuleInput | null;
  onChange: (v: Partial<MetricFilterCriterionFragment>) => void;
}) => {
  return (
    <ChipInput
      placeholder="string"
      type="text"
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        onChange({
          attribute: {
            attribute,
            likeRule: { pattern: e.target.value },
          },
        });
      }}
      value={value?.pattern ?? ''}
    />
  );
};

const isRangeValue = (value?: OperatorOption): value is RangeOperatorOption =>
  !!value && value.ruleType === 'rangeRule';

export const MetricFilterRangeRule = ({
  value,
  attribute,
  valueType,
  onChange,
  operator,
  column,
}: RuleInputProps<MetricsV1FilterRangeRule> & {
  operator?: OperatorOption;
}) => {
  const hll = column?.type === MetricsV1ColumnType.ColumnTypeHllSketch;
  if (!isRangeValue(operator)) return null;

  const keys = Object.keys(
    ObjUtils.cleanNulls(value ?? {}),
  ) as (keyof MetricsV1FilterRangeRule)[];

  const startKey = keys.find(k => k.startsWith('start'));
  const endKey = keys.find(k => k.startsWith('end'));

  const startValue = startKey
    ? (value?.[startKey]?.[valueType]?.toString() ?? '')
    : null;
  const endValue = endKey
    ? (value?.[endKey]?.[valueType]?.toString() ?? '')
    : null;

  const showStart = !isNil(startValue) && startKey;
  const showEnd = !isNil(endValue) && endKey;
  const isClosedRange = showStart && showEnd;
  const defaultProps = {
    placeholder: valueType.replace('Value', ''),
    type: valueType === 'numberValue' ? 'number' : 'text',
  };
  const defaultEnd = showEnd
    ? {
        [endKey]: {
          [valueType]: formatValue(valueType, hll)(endValue),
        },
      }
    : {};

  const defaultStart = showStart
    ? {
        [startKey]: {
          [valueType]: formatValue(valueType, hll)(startValue),
        },
      }
    : {};

  const handleChange = (v: MetricsV1FilterRangeRule) => {
    onChange({
      attribute: {
        attribute,
        rangeRule: v,
      },
    });
  };

  return (
    <>
      {showStart && (
        <>
          {valueType === 'timestampValue' ? (
            <ChipTimestampPicker
              maxWidth={120}
              displayFormat="yyyy-MM-dd HH:mm"
              InputProps={{
                endAdornment: isClosedRange && (
                  <InclusionPicker
                    value={startKey}
                    onChange={newKey => {
                      handleChange({
                        [newKey]: {
                          [valueType]: formatValue(valueType)(startValue),
                        },
                        ...defaultEnd,
                      });
                    }}
                  />
                ),
              }}
              value={startValue ? startValue.toString() : null}
              onChange={v => {
                handleChange({
                  [startKey]: {
                    [valueType]: formatValue(valueType)(v),
                  },
                  ...defaultEnd,
                });
              }}
            />
          ) : (
            <ChipInput
              {...defaultProps}
              type={valueType === 'numberValue' ? 'number' : 'text'}
              inputProps={hll ? { step: '1' } : undefined}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleChange({
                  [startKey]: {
                    [valueType]: formatValue(valueType, hll)(e.target.value),
                  },
                  ...defaultEnd,
                });
              }}
              value={startValue}
              InputProps={{
                endAdornment: isClosedRange && (
                  <InclusionPicker
                    value={startKey}
                    onChange={newKey => {
                      handleChange({
                        [newKey]: {
                          [valueType]: formatValue(valueType, hll)(startValue),
                        },
                        ...defaultEnd,
                      });
                    }}
                  />
                ),
              }}
            />
          )}
        </>
      )}
      {isClosedRange && <span> and </span>}
      {showEnd && (
        <>
          {valueType === 'timestampValue' ? (
            <ChipTimestampPicker
              maxWidth={120}
              displayFormat="yyyy-MM-dd HH:mm"
              InputProps={{
                endAdornment: isClosedRange && (
                  <InclusionPicker
                    value={endKey}
                    onChange={newKey => {
                      handleChange({
                        [newKey]: {
                          [valueType]: formatValue(valueType)(endValue),
                        },
                        ...defaultStart,
                      });
                    }}
                  />
                ),
              }}
              value={endValue ? endValue.toString() : null}
              onChange={v => {
                handleChange({
                  [endKey]: {
                    [valueType]: formatValue(valueType)(v),
                  },
                  ...defaultStart,
                });
              }}
            />
          ) : (
            <ChipInput
              {...defaultProps}
              inputProps={hll ? { step: '1' } : undefined}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleChange({
                  [endKey]: {
                    [valueType]: formatValue(valueType, hll)(e.target.value),
                  },
                  ...defaultStart,
                });
              }}
              InputProps={{
                endAdornment: isClosedRange && (
                  <InclusionPicker
                    value={endKey}
                    onChange={newKey => {
                      handleChange({
                        [newKey]: {
                          [valueType]: formatValue(valueType, hll)(endValue),
                        },
                        ...defaultStart,
                      });
                    }}
                  />
                ),
              }}
              value={endValue}
            />
          )}
        </>
      )}
    </>
  );
};

function InclusionPicker({
  value,
  onChange,
}: {
  value: keyof MetricsV1FilterRangeRule;
  onChange: (k: keyof MetricsV1FilterRangeRule) => void;
}) {
  const handleChange = (v: string) => {
    return () => {
      const prefix = value.replace('Inclusive', '').replace('Exclusive', '');
      const newKey = `${prefix}${v}` as keyof MetricsV1FilterRangeRule;
      onChange(newKey);
    };
  };
  const options = ['Inclusive', 'Exclusive'];
  return (
    <ContextMenu
      closeOnSelect
      renderButton={p => (
        <Button
          {...p}
          endIcon={<ExpandMore color="inherit" />}
          size="small"
          variant="text"
        >
          {options.find(o => value.includes(o))?.slice(0, 3)}
        </Button>
      )}
      renderMenu={() =>
        options.map(option => (
          <MenuItem key={option} divider onClick={handleChange(option)}>
            {option}
          </MenuItem>
        ))
      }
    />
  );
}
